import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MessageCircle, Bell, Calendar, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { useCompanySettings } from '../hooks/useCompanySettings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return '-'; } };

const buildWaNumber = (phone) => {
  let digits = (phone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length <= 11) digits = `55${digits}`;
  return digits;
};

const FILTERS = [
  { key: 'today', label: 'Vencem Hoje', icon: Calendar },
  { key: 'overdue', label: 'Vencidas', icon: AlertTriangle },
  { key: 'week', label: 'Próx. 7 dias', icon: Bell },
  { key: 'all', label: 'Todas Pendentes', icon: Bell },
];

export const CollectionReminders = () => {
  const company = useCompanySettings();
  const [filter, setFilter] = useState('today');
  const [data, setData] = useState({ count: 0, total: 0, items: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (f) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/accounts/receivables/collection-reminders?filter=${f}`, { withCredentials: true });
      setData(res.data);
    } catch {
      setData({ count: 0, total: 0, items: [] });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const buildMessage = (it) => {
    const trade = company?.trade_name || 'Smart Flow';
    const venc = fmtDate(it.due_date);
    const atraso = it.days_overdue > 0 ? ` (vencida há ${it.days_overdue} dias)` : '';
    return `Olá ${it.customer_name}! 😊\n\nPassando para lembrar sobre seu fiado na *${trade}*:\n\n📄 ${it.description || 'Compra a prazo'}\n💰 Valor: ${fmt(it.total_with_interest || it.amount)}\n📅 Vencimento: ${venc}${atraso}\n\nQualquer dúvida estamos à disposição. Obrigado!`;
  };

  const sendWhatsApp = (it) => {
    const num = buildWaNumber(it.phone);
    const text = encodeURIComponent(buildMessage(it));
    const url = num ? `https://wa.me/${num}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, '_blank');
  };

  return (
    <div data-testid="collection-reminders">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {FILTERS.map((f) => {
          const Icon = f.icon;
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-bold transition-colors ${active ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}
              data-testid={`reminder-filter-${f.key}`}
            >
              <Icon size={15} /> {f.label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-md border border-slate-200 p-4">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Cobranças ({FILTERS.find(f => f.key === filter)?.label})</p>
          <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="reminder-count">{data.count} conta(s)</p>
        </div>
        <div className="bg-white rounded-md border border-slate-200 p-4">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Valor Total</p>
          <p className="text-2xl font-bold text-amber-600 mt-1" data-testid="reminder-total">{fmt(data.total)}</p>
        </div>
      </div>

      <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Vencimento</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Cliente</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Descrição</th>
              <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Valor</th>
              <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Cobrar</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200" data-testid="reminder-list">
            {loading && <tr><td colSpan={5} className="text-center py-8 text-slate-400">Carregando...</td></tr>}
            {!loading && data.items.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-400" data-testid="reminder-empty">Nenhuma cobrança nesse período 🎉</td></tr>}
            {!loading && data.items.map((it) => (
              <tr key={it.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-sm">
                  {fmtDate(it.due_date)}
                  {it.days_overdue > 0 && <div className="text-xs text-red-600 font-bold">{it.days_overdue} dias atraso</div>}
                </td>
                <td className="px-4 py-3 text-sm font-medium text-slate-900">
                  {it.customer_name}
                  {!it.phone && <div className="text-[11px] text-amber-600">sem telefone</div>}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">{it.description || 'Compra a prazo'}</td>
                <td className="px-4 py-3 text-right font-bold text-slate-900">{fmt(it.total_with_interest || it.amount)}</td>
                <td className="px-4 py-3 text-center">
                  <Button
                    size="sm"
                    onClick={() => sendWhatsApp(it)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid={`reminder-whatsapp-${it.id}`}
                  >
                    <MessageCircle size={15} className="mr-1" /> WhatsApp
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
